import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useStrategyHoldings,
  type EnrichedHoldingRow,
} from "../../hooks/useStrategyHoldings";
import { productSearchService } from "../../services/productSearchService";
import type {
  ManagedStrategySearchItem,
  ProductSearchItem,
} from "../../types/productV3";
import {
  extractProductContracts,
  getProductReferencedBasketIds,
  type StrategyHoldingsSummary,
  type StrategyPositionInput,
} from "../../utils/strategyFlatPositions";
import {
  expandAlphaStrategyProjection,
  getAlphaManagedCurrentComposition,
  isValidAlphaManagedChild,
  resolveAlphaStrategyWeight,
  type AlphaProjectionIssue,
  type AlphaStrategyPositionInput,
} from "./alphaManagedStrategyProjection";

export type UseAlphaStrategyHoldingsOptions =
  | { enabled?: boolean; accessMode?: "investor" }
  | { enabled?: boolean; accessMode: "admin"; ownerUserId: string };

export interface UseAlphaStrategyHoldingsResult {
  holdings: EnrichedHoldingRow[];
  summary: StrategyHoldingsSummary;
  missingProducts: string[];
  complete: boolean;
  issues: AlphaProjectionIssue[];
  expandedEntries: StrategyPositionInput[];
  resolvedProductItems: ProductSearchItem[];
  resolvedBasketLookup: Map<number, ProductSearchItem>;
  isLoading: boolean;
  error: Error | null;
  retry: () => void;
}

type ProductEntityType = "PORTFOLIO" | "BASKET";

interface ResolverPlan {
  baseItems: Map<string, ProductSearchItem>;
  activeEntries: AlphaStrategyPositionInput[];
  managedIds: number[];
  missingDirect: Array<{
    entityType: ProductEntityType;
    productId: number;
  }>;
  missingKnownNested: Set<number>;
  requiresFetch: boolean;
}

interface ResolverState {
  key: string | null;
  loadedManaged: Map<number, ManagedStrategySearchItem>;
  loadedProducts: ProductSearchItem[];
  isResolving: boolean;
  error: Error | null;
}

function itemKey(entityType: ProductEntityType, productId: number): string {
  return `${entityType}-${productId}`;
}

function buildItemMap(
  productItems: ProductSearchItem[],
  basketLookup: Map<number, ProductSearchItem>
): Map<string, ProductSearchItem> {
  const map = new Map<string, ProductSearchItem>();
  for (const item of productItems) {
    map.set(itemKey(item.productType, item.productId), item);
  }
  for (const item of basketLookup.values()) {
    if (item.productType === "BASKET") {
      map.set(itemKey("BASKET", item.productId), item);
    }
  }
  return map;
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

async function fetchAdminProduct(
  entityType: ProductEntityType,
  productId: number,
  signal?: AbortSignal
): Promise<ProductSearchItem> {
  const response = await productSearchService.searchAdminProducts({
    page: 1,
    pageSize: 1,
    productType: entityType,
    productId,
  }, signal);
  const item = response.items.find((candidate) => (
    candidate.productType === entityType
    && candidate.productId === productId
  ));
  if (!item) {
    throw new Error(`${entityType} ${productId} was not returned by admin product search.`);
  }
  return item;
}

function isPartialSnapshot(item: ProductSearchItem): boolean {
  return (
    item.snapshot !== null &&
    typeof item.snapshot === "object" &&
    "partial" in item.snapshot &&
    item.snapshot.partial === true
  );
}

function buildResolverPlan(
  entries: AlphaStrategyPositionInput[],
  productItems: ProductSearchItem[],
  basketLookup: Map<number, ProductSearchItem>
): ResolverPlan {
  const baseItems = buildItemMap(productItems, basketLookup);
  const activeEntries = entries.filter((entry) => {
    const weight = resolveAlphaStrategyWeight(entry);
    return Number.isFinite(weight) && weight !== 0;
  });
  const managedIds = Array.from(new Set(
    activeEntries
      .filter((entry) => entry.entityType === "MANAGED_STRATEGY")
      .map((entry) => entry.productId)
  ));
  const missingDirectByKey = new Map<string, {
    entityType: ProductEntityType;
    productId: number;
  }>();
  for (const entry of activeEntries) {
    if (entry.entityType === "MANAGED_STRATEGY") continue;
    const key = itemKey(entry.entityType, entry.productId);
    if (!baseItems.has(key)) {
      missingDirectByKey.set(key, {
        entityType: entry.entityType,
        productId: entry.productId,
      });
    }
  }
  const missingDirect = Array.from(missingDirectByKey.values());
  const missingKnownNested = new Set<number>();
  for (const entry of activeEntries) {
    if (entry.entityType === "MANAGED_STRATEGY") continue;
    const item = baseItems.get(itemKey(entry.entityType, entry.productId));
    if (!item) continue;
    for (const basketId of getProductReferencedBasketIds(item)) {
      if (!baseItems.has(itemKey("BASKET", basketId))) {
        missingKnownNested.add(basketId);
      }
    }
  }

  return {
    baseItems,
    activeEntries,
    managedIds,
    missingDirect,
    missingKnownNested,
    requiresFetch:
      managedIds.length > 0 ||
      missingDirect.length > 0 ||
      missingKnownNested.size > 0,
  };
}

export function useAlphaStrategyHoldings(
  entries: AlphaStrategyPositionInput[],
  productItems: ProductSearchItem[],
  basketLookup: Map<number, ProductSearchItem>,
  options: UseAlphaStrategyHoldingsOptions = {}
): UseAlphaStrategyHoldingsResult {
  const enabled = options.enabled ?? true;
  const accessMode = options.accessMode === "admin" ? "admin" : "investor";
  const ownerUserId = options.accessMode === "admin" ? options.ownerUserId : "";
  const [resolverState, setResolverState] = useState<ResolverState>({
    key: null,
    loadedManaged: new Map(),
    loadedProducts: [],
    isResolving: false,
    error: null,
  });
  const [retryCount, setRetryCount] = useState(0);
  const requestIdRef = useRef(0);

  const entryLoadKey = useMemo(
    () => entries.map((entry) => {
      const weight = resolveAlphaStrategyWeight(entry);
      return `${entry.entityType}-${entry.productId}-${Number.isFinite(weight) && weight !== 0 ? 1 : 0}`;
    }).join("|"),
    [entries]
  );
  const catalogLoadKey = useMemo(() => {
    const keys = productItems.map((item) => (
      `${item.productType}-${item.productId}-${item.updatedAtUtc}-${
        isPartialSnapshot(item) ? 1 : 0
      }`
    ));
    for (const item of basketLookup.values()) {
      keys.push(`BASKET-${item.productId}-${item.updatedAtUtc}`);
    }
    return keys.sort().join("|");
  }, [productItems, basketLookup]);
  const resolverLoadKey = `${enabled ? 1 : 0}|${accessMode}|${ownerUserId}|${entryLoadKey}|${catalogLoadKey}|${retryCount}`;
  const resolverPlan = buildResolverPlan(entries, productItems, basketLookup);
  const resolverStateIsCurrent = resolverState.key === resolverLoadKey;
  const loadedManaged = resolverStateIsCurrent
    ? resolverState.loadedManaged
    : new Map<number, ManagedStrategySearchItem>();
  const loadedProducts = resolverStateIsCurrent ? resolverState.loadedProducts : [];
  const resolverError = resolverStateIsCurrent ? resolverState.error : null;
  const isResolvingCurrentKey =
    enabled &&
    resolverPlan.requiresFetch &&
    (!resolverStateIsCurrent || resolverState.isResolving);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    const {
      baseItems,
      activeEntries,
      managedIds,
      missingDirect,
      missingKnownNested,
      requiresFetch,
    } = buildResolverPlan(entries, productItems, basketLookup);

    if (!enabled) {
      setResolverState({
        key: resolverLoadKey,
        loadedManaged: new Map(),
        loadedProducts: [],
        isResolving: false,
        error: null,
      });
      return () => controller.abort();
    }

    if (!requiresFetch) {
      setResolverState({
        key: resolverLoadKey,
        loadedManaged: new Map(),
        loadedProducts: [],
        isResolving: false,
        error: null,
      });
      return () => controller.abort();
    }

    setResolverState({
      key: resolverLoadKey,
      loadedManaged: new Map(),
      loadedProducts: [],
      isResolving: true,
      error: null,
    });

    void (async () => {
      const managedMap = new Map<number, ManagedStrategySearchItem>();
      const itemMap = new Map(baseItems);
      const fetchedProducts = new Map<string, ProductSearchItem>();
      const productRequests = new Map<string, Promise<void>>();
      let failed = false;

      const fetchProduct = (
        entityType: ProductEntityType,
        productId: number
      ): Promise<void> => {
        const key = itemKey(entityType, productId);
        if (itemMap.has(key)) return Promise.resolve();
        const existingRequest = productRequests.get(key);
        if (existingRequest) return existingRequest;
        const request = (async () => {
          try {
            const item = accessMode === "admin"
              ? await fetchAdminProduct(entityType, productId, controller.signal)
              : await productSearchService.getProductForInvestor(
                entityType,
                productId,
                controller.signal
              );
            if (controller.signal.aborted) return;
            itemMap.set(key, item);
            fetchedProducts.set(key, item);
          } catch (error) {
            if (!isAbortError(error) && !controller.signal.aborted) failed = true;
          }
        })();
        productRequests.set(key, request);
        return request;
      };

      await Promise.all([
        ...managedIds.map(async (managedId) => {
          try {
            const item = accessMode === "admin"
              ? await productSearchService.getManagedStrategyForAdmin(
                managedId,
                controller.signal
              )
              : await productSearchService.getManagedStrategyForInvestor(
                managedId,
                controller.signal
              );
            if (!controller.signal.aborted) managedMap.set(managedId, item);
          } catch (error) {
            if (!isAbortError(error) && !controller.signal.aborted) failed = true;
          }
        }),
        ...missingDirect.map((entry) => fetchProduct(entry.entityType, entry.productId)),
        ...Array.from(missingKnownNested).map((basketId) => (
          fetchProduct("BASKET", basketId)
        )),
      ]);
      if (controller.signal.aborted || requestId !== requestIdRef.current) return;

      const relevantProductKeys = new Set<string>();
      for (const entry of activeEntries) {
        if (entry.entityType !== "MANAGED_STRATEGY") {
          relevantProductKeys.add(itemKey(entry.entityType, entry.productId));
          continue;
        }
        const managed = managedMap.get(entry.productId);
        if (!managed) continue;
        const composition = getAlphaManagedCurrentComposition(managed);
        if (!composition) continue;
        for (const child of composition) {
          if (!isValidAlphaManagedChild(child)) continue;
          relevantProductKeys.add(itemKey(child.entityType, child.productId));
        }
      }

      await Promise.all(Array.from(relevantProductKeys).map((key) => {
        if (itemMap.has(key)) return Promise.resolve();
        const separator = key.lastIndexOf("-");
        const entityType = key.slice(0, separator) as ProductEntityType;
        const productId = Number(key.slice(separator + 1));
        return fetchProduct(entityType, productId);
      }));
      if (controller.signal.aborted || requestId !== requestIdRef.current) return;

      const nestedBasketIds = new Set<number>();
      for (const key of relevantProductKeys) {
        const item = itemMap.get(key);
        if (!item) continue;
        for (const basketId of getProductReferencedBasketIds(item)) {
          if (!itemMap.has(itemKey("BASKET", basketId))) {
            nestedBasketIds.add(basketId);
          }
        }
      }
      await Promise.all(Array.from(nestedBasketIds).map((basketId) => (
        fetchProduct("BASKET", basketId)
      )));
      if (controller.signal.aborted || requestId !== requestIdRef.current) return;

      setResolverState({
        key: resolverLoadKey,
        loadedManaged: managedMap,
        loadedProducts: Array.from(fetchedProducts.values()),
        isResolving: false,
        error: failed ? new Error("Some strategy holdings could not be loaded.") : null,
      });
    })();

    return () => controller.abort();
  }, [resolverLoadKey]);

  const resolvedProductItems = useMemo(() => {
    const map = buildItemMap(productItems, basketLookup);
    for (const item of loadedProducts) {
      const key = itemKey(item.productType, item.productId);
      if (!map.has(key)) map.set(key, item);
    }
    return Array.from(map.values());
  }, [productItems, basketLookup, loadedProducts]);

  const resolvedBasketLookup = useMemo(() => {
    const map = new Map<number, ProductSearchItem>();
    for (const item of resolvedProductItems) {
      if (item.productType !== "BASKET") continue;
      const validation = extractProductContracts(item, new Map(), {
        strictDependencies: true,
      });
      // A partial subscriber snapshot must never feed contract math. Leaving
      // it out also lets a parent Portfolio keep its safe direct contracts
      // while reporting the unresolved nested Basket.
      if (validation.complete) map.set(item.productId, item);
    }
    return map;
  }, [resolvedProductItems]);

  const projection = useMemo(
    () => enabled
      ? expandAlphaStrategyProjection(
          entries,
          loadedManaged,
          resolvedProductItems,
          resolvedBasketLookup
        )
      : { expandedEntries: [], complete: true, issues: [] },
    [enabled, entries, loadedManaged, resolvedProductItems, resolvedBasketLookup]
  );

  const leafHoldings = useStrategyHoldings(
    projection.expandedEntries,
    resolvedProductItems,
    resolvedBasketLookup,
    { reportPendingBeforeEffect: true }
  );

  const retryLeafHoldings = leafHoldings.retry;
  const retry = useCallback(() => {
    setRetryCount((count) => count + 1);
    retryLeafHoldings();
  }, [retryLeafHoldings]);

  return {
    holdings: leafHoldings.holdings,
    summary: leafHoldings.summary,
    missingProducts: leafHoldings.missingProducts,
    complete: projection.complete && resolverError === null,
    issues: projection.issues,
    expandedEntries: projection.expandedEntries,
    resolvedProductItems,
    resolvedBasketLookup,
    isLoading: enabled && (isResolvingCurrentKey || leafHoldings.isLoading),
    error: resolverError ?? leafHoldings.error,
    retry,
  };
}

export type { AlphaStrategyPositionInput } from "./alphaManagedStrategyProjection";
